import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOrders } from "@/context/OrdersContext";
import { useBranch } from "@/context/BranchContext";
import { printOrderReceipt } from "@/pages/branch/orders/printReceipt";
import { PAYMENT_METHODS, PICKUP_METHODS, ORDER_STATUS } from "@/pages/branch/orders/constants";
import InvoiceCard from "@/pages/branch/orders/components/InvoiceCard";
import AccessDenied from "@/pages/branch/access-denied/page";

export default function PosSuccessPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useBranch();
  const { orders, loading } = useOrders();
  const [showInvoice, setShowInvoice] = useState(false);

  const order = orders.find((o) => o.id === id) ?? null;

  if (!can("pos")) {
    return <AccessDenied />;
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-background-50 rounded-card border border-background-200 p-10 flex flex-col items-center justify-center animate-pulse">
          <div className="w-20 h-20 rounded-full bg-background-200"></div>
          <div className="mt-4 h-5 w-48 bg-background-200 rounded"></div>
          <div className="mt-3 h-4 w-32 bg-background-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-background-50 rounded-card border border-background-200 p-10 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="mt-4 text-lg font-bold text-foreground-900">لم يتم العثور على الطلب</h2>
          <p className="mt-1.5 text-sm text-foreground-500 max-w-sm">
            قد يكون الطلب قد أُزيل أو انتهت صلاحية عرضه. يمكنك العودة لشاشة الكاشير لإنشاء طلب جديد.
          </p>
          <button
            type="button"
            onClick={() => navigate("/pos")}
            className="mt-5 h-11 px-6 rounded-btn bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            العودة للكاشير
          </button>
        </div>
      </div>
    );
  }

  const status = ORDER_STATUS[order.status];

  return (
    <div className="max-w-2xl mx-auto space-y-4 animate-fade-slide-up">
      {/* شريط الرجوع */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate("/pos")}
          className="inline-flex items-center gap-1.5 h-10 pl-2 pr-3 rounded-btn text-sm font-semibold text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-line text-xl"></i>
          العودة للكاشير
        </button>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold whitespace-nowrap">
          <i className="ri-checkbox-circle-line"></i>
          تم إنشاء الطلب
        </span>
      </div>

      {/* بطاقة النجاح */}
      <div className="bg-background-50 rounded-card border border-background-200 p-6 sm:p-7">
        <div className="text-center">
          <div className="mx-auto w-20 h-20 rounded-full bg-primary-500 flex items-center justify-center animate-pop-check">
            <i className="ri-check-line text-4xl text-white"></i>
          </div>
          <h2 className="mt-4 text-xl md:text-2xl font-bold text-foreground-900">
            تم إنشاء الطلب #{order.number}
          </h2>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-100 border border-background-200">
            <i className={`${status.icon} text-sm`} style={{ color: status.color }}></i>
            <span className="text-xs font-bold" style={{ color: status.color }}>
              {status.label}
            </span>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-btn bg-background-100 border border-background-200 p-3">
              <p className="text-[11px] text-foreground-500">الاستلام</p>
              <p className="text-sm font-bold text-foreground-900 mt-0.5">
                {PICKUP_METHODS[order.pickupMethod].label}
              </p>
            </div>
            <div className="rounded-btn bg-background-100 border border-background-200 p-3">
              <p className="text-[11px] text-foreground-500">الدفع</p>
              <p className="text-sm font-bold text-foreground-900 mt-0.5">
                {PAYMENT_METHODS[order.paymentMethod].short}
              </p>
            </div>
            <div className="rounded-btn bg-background-100 border border-background-200 p-3">
              <p className="text-[11px] text-foreground-500">الإجمالي</p>
              <p className="text-sm font-bold text-foreground-900 mt-0.5" dir="ltr">
                {order.total.toFixed(2)}
              </p>
            </div>
          </div>

          <p className="mt-4 text-xs text-foreground-500 leading-relaxed">
            تمت إضافة الطلب إلى قائمة الطلبات فورًا، واحتسابه في إحصائيات وتقارير نقطة البيع.
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => setShowInvoice((v) => !v)}
            className="w-full h-12 rounded-btn bg-background-100 text-foreground-800 border border-background-300 text-sm font-bold flex items-center justify-center gap-2 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={`ri-${showInvoice ? "arrow-up" : "receipt"}-line text-lg`}></i>
            {showInvoice ? "إخفاء الفاتورة" : "عرض الفاتورة"}
          </button>
          <button
            type="button"
            onClick={() => printOrderReceipt(order)}
            className="w-full h-12 rounded-btn bg-primary-500 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-printer-line text-lg"></i>
            طباعة الفاتورة
          </button>
          <div className="grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={() => navigate("/pos")}
              className="h-12 rounded-btn bg-background-100 text-foreground-700 border border-background-300 text-sm font-bold flex items-center justify-center gap-2 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-lg"></i>
              طلب جديد
            </button>
            <button
              type="button"
              onClick={() => navigate("/orders")}
              className="h-12 rounded-btn bg-foreground-900 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line text-lg"></i>
              الطلبات
            </button>
          </div>
        </div>
      </div>

      {/* الفاتورة */}
      {showInvoice ? (
        <div className="animate-fade-slide-up">
          <InvoiceCard order={order} />
        </div>
      ) : null}
    </div>
  );
}